/**
 * Serviço de apurações.
 * Listagem: api-relatorios GET /relatorios/apuracoes.
 * Criar: POST /relatorios/apurar (Kafka → api-relatorio-apuracao).
 * Adicionar período: mesma rota POST /relatorios/apurar (modo atualizar na api-relatorios).
 */

import {
  apurarRelatorio as apurarRelatorioApi,
  atualizarBaseLegalSumarizacao as atualizarBaseLegalSumarizacaoApi,
  buscarBasesLegais as buscarBasesLegaisApi,
  exportarSumarizacaoApuracao as exportarSumarizacaoApuracaoApi,
  reprocessarSumarizacaoApuracao as reprocessarSumarizacaoApuracaoApi,
  reprocessarAnosApuracao as reprocessarAnosApuracaoApi,
  processarSumarizacaoApuracao as processarSumarizacaoApuracaoApi,
  atualizarSelicApuracao as atualizarSelicApuracaoApi,
  buscarApuracaoPorId as buscarApuracaoPorIdApi,
  buscarSumarizacaoApuracao as buscarSumarizacaoApuracaoApi,
  buscarSumarizacaoIcmsApuracao as buscarSumarizacaoIcmsApuracaoApi,
  buscarSumarizacaoNotasFiscaisApuracao as buscarSumarizacaoNotasFiscaisApuracaoApi,
  buscarLancamentosContaApuracao as buscarLancamentosContaApuracaoApi,
  buscarLancamentosCat42Apuracao as buscarLancamentosCat42ApuracaoApi,
  buscarClassificacaoNcmApuracao as buscarClassificacaoNcmApuracaoApi,
  buscarLancamentosIcmsE111Apuracao as buscarLancamentosIcmsE111ApuracaoApi,
  buscarLancamentosNotasFiscaisApuracao as buscarLancamentosNotasFiscaisApuracaoApi,
  buscarSumarizacaoEcdApuracao as buscarSumarizacaoEcdApuracaoApi,
  buscarLancamentosEcdApuracao as buscarLancamentosEcdApuracaoApi,
  buscarSumarizacaoEcfApuracao as buscarSumarizacaoEcfApuracaoApi,
  buscarLancamentosEcfApuracao as buscarLancamentosEcfApuracaoApi,
  buscarSumarizacaoIcmsStApuracao as buscarSumarizacaoIcmsStApuracaoApi,
  buscarSumarizacaoContribuicoesApuracao as buscarSumarizacaoContribuicoesApuracaoApi,
  buscarLancamentosContribuicoesApuracao as buscarLancamentosContribuicoesApuracaoApi,
  buscarSumarizacaoEcredacApuracao as buscarSumarizacaoEcredacApuracaoApi,
  buscarNotasEcredacApuracao as buscarNotasEcredacApuracaoApi,
  exportarNotasEcredacApuracao as exportarNotasEcredacApuracaoApi,
  buscarClassificacaoSugeridaEcredac as buscarClassificacaoSugeridaEcredacApi,
  baixarExportacaoApuracao,
  consultarStatusExportacao as consultarStatusExportacaoApi,
  baixarArquivoExportacao as baixarArquivoExportacaoApi,
  listarApuracoes as listarApuracoesApi,
  buscarAgrupadoEsocial as buscarAgrupadoEsocialApi,
  buscarDashboardEsocial as buscarDashboardEsocialApi,
  buscarRubricasEsocial as buscarRubricasEsocialApi,
  atualizarRubricaEsocial as atualizarRubricaEsocialApi,
  buscarLancamentosEsocial as buscarLancamentosEsocialApi,
  buscarCalculo20sEsocial as buscarCalculo20sEsocialApi,
  buscarSefipEsocial as buscarSefipEsocialApi,
  exportarEsocial as exportarEsocialApi,
} from "./api-relatorios.service";

function normCnpj(s) {
  return String(s || "").replace(/\D/g, "");
}

/**
 * @param {Record<string, string|number|undefined>} params
 */
export async function listarApuracoes(params = {}) {
  const limit = Math.max(1, parseInt(String(params.limit || 10), 10) || 10);
  let page = parseInt(String(params.page ?? ""), 10);
  if (!Number.isFinite(page) || page < 1) {
    if (params.offset != null) {
      const offset = Math.max(0, parseInt(String(params.offset || 0), 10) || 0);
      page = Math.floor(offset / limit) + 1;
    } else {
      page = 1;
    }
  }

  const apiParams = {
    page,
    limit,
  };
  if (params.q_empresa) apiParams.q_empresa = params.q_empresa;
  if (params.empresa) apiParams.q_empresa = params.empresa;
  if (params.cnpj) apiParams.cnpj = params.cnpj;
  if (params.tipo) apiParams.tipo = params.tipo;
  if (params.status) apiParams.status = params.status;

  try {
    const res = await listarApuracoesApi(apiParams);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível carregar as apurações.",
        itens: [],
        total: 0,
      };
    }
    return {
      sucesso: true,
      itens: Array.isArray(res?.itens) ? res.itens : [],
      total: typeof res?.total === "number" ? res.total : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar apurações.";
    return { sucesso: false, mensagem, itens: [], total: 0 };
  }
}

/**
 * Dispara apuração (V3 / Conciliação SPED) via api-relatorios — sem api-relatorio-apuracao na borda.
 * Body HTTP (fase 5): blocos `apuracao` / `sumarizacao`; sem `codigo_relatorio` nem `contas_resultado`.
 * @param {object} body
 * @param {string} body.cnpj
 * @param {string} body.periodo_inicio
 * @param {string} body.periodo_fim
 * @param {{ id_relatorio: number }} body.configuracoes
 * @param {{ contas_criterio?: 'resultado'|'todas' }} [body.apuracao]
 * @param {{ termos_exclusao?: string[] }} [body.sumarizacao]
 */
export async function criarApuracao(body) {
  const idRelatorio = body?.configuracoes?.id_relatorio;
  if (idRelatorio == null || !Number.isFinite(Number(idRelatorio))) {
    return {
      sucesso: false,
      mensagem: "Relatório de apuração inválido.",
    };
  }

  const cnpj = normCnpj(body.cnpj);
  if (!cnpj || cnpj.length !== 14) {
    return { sucesso: false, mensagem: "CNPJ inválido." };
  }

  // `id_relatorio`/`codigo_relatorio` são descartados de propósito: a identidade do relatório
  // vai só em `idRelatorio` (ver teste "omite codigo_relatorio no POST"). O resto de
  // `configuracoes` (flags específicas do tipo, ex. ICMS/CAT-42) é o que a API repassa no
  // envelope Kafka como `configuracoes` (Record<string, boolean>) — sem esta extração, essas
  // chaves eram descartadas no browser antes do POST.
  const { id_relatorio, codigo_relatorio, ...configuracoesExtras } = body.configuracoes || {};

  const payload = {
    idRelatorio: Number(idRelatorio),
    cnpj,
    periodo_inicio: body.periodo_inicio,
    periodo_fim: body.periodo_fim,
  };

  if (Object.keys(configuracoesExtras).length > 0) {
    payload.configuracoes = configuracoesExtras;
  }

  const criterio = String(body?.apuracao?.contas_criterio ?? "").trim();
  if (criterio === "resultado" || criterio === "todas") {
    payload.apuracao = { contas_criterio: criterio };
  }

  if (body?.sumarizacao != null && typeof body.sumarizacao === "object") {
    const termos = Array.isArray(body.sumarizacao.termos_exclusao)
      ? body.sumarizacao.termos_exclusao
      : [];
    payload.sumarizacao = { termos_exclusao: termos };
  }

  try {
    const res = await apurarRelatorioApi(payload);

    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível enfileirar a apuração.",
      };
    }

    return {
      sucesso: true,
      mensagem:
        res?.mensagem ||
        "Pedido de apuração recebido para processamento.",
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar apuração.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Detalhe da apuração (cabeçalho + jobs na fila + apurações filhas vinculadas).
 * TXM-418 (Apuração ICMS) — `filhos` é usado pela tela de detalhe do tipo ICMS para
 * localizar a apuração CAT-42 vinculada e alimentar a aba "ICMS Cat-42".
 * @param {string|number} id
 */
export async function buscarApuracaoDetalhe(id) {
  try {
    const res = await buscarApuracaoPorIdApi(id);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Apuração não encontrada.",
      };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      processamentos: Array.isArray(res?.processamentos) ? res.processamentos : [],
      filhos: Array.isArray(res?.filhos) ? res.filhos : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar a apuração.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-418 — sumarização das apurações de ICMS (aba ICMS/E110 e CAT-42). Endpoint
 * separado do genérico: o layout de linha é próprio de cada motor, então o backend
 * tem serviço e rota dedicados, no mesmo padrão adotado para o eSocial.
 * @param {string|number} id
 */
export async function buscarSumarizacaoIcmsApuracao(id) {
  return consultarSumarizacao(() => buscarSumarizacaoIcmsApuracaoApi(id));
}

/**
 * TXM-418/TXM-597 — sumarização das apurações de Notas Fiscais (competência + tipo).
 * @param {string|number} id
 */
export async function buscarSumarizacaoNotasFiscaisApuracao(id) {
  return consultarSumarizacao(() => buscarSumarizacaoNotasFiscaisApuracaoApi(id));
}

/**
 * TXM-418/TXM-598 — sumarização das apurações de ECD (razão contábil por conta e
 * competência).
 * @param {string|number} id
 */
export async function buscarSumarizacaoEcdApuracao(id) {
  return consultarSumarizacao(() => buscarSumarizacaoEcdApuracaoApi(id));
}

/**
 * TXM-418/TXM-599 — sumarização das apurações de ECF (apuração anual do IRPJ/CSLL, grão
 * ano — fechamento "A00" dos blocos M/N do SPED ECF).
 * @param {string|number} id
 */
export async function buscarSumarizacaoEcfApuracao(id) {
  return consultarSumarizacao(() => buscarSumarizacaoEcfApuracaoApi(id));
}

/**
 * TXM-473 — sumarização da apuração ICMS-ST (gerencial): cruzamento entrada×saída por
 * competência/NCM/EAN/modelo/origem. Relatório distinto do CAT-42 (ICMS_ST_CAT42) —
 * mesmo imposto, layout de linha próprio.
 * Sem `opcoes.projeto` os itens já utilizados em projetos aprovados ficam de fora; com ele, a API
 * devolve só os itens daquele projeto (TXM-558).
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [opcoes]
 */
export async function buscarSumarizacaoIcmsStApuracao(id, opcoes) {
  return consultarSumarizacao(() =>
    opcoes === undefined
      ? buscarSumarizacaoIcmsStApuracaoApi(id)
      : buscarSumarizacaoIcmsStApuracaoApi(id, opcoes)
  );
}

/**
 * TXM-600 — sumarização da apuração Contribuições: blocos M210/M200/M610/M600/F100/C170
 * do SPED Contribuições (PIS/COFINS) por competência.
 * @param {string|number} id
 */
export async function buscarSumarizacaoContribuicoesApuracao(id) {
  return consultarSumarizacao(() => buscarSumarizacaoContribuicoesApuracaoApi(id));
}

/**
 * TXM-418/TXM-475 — sumarização da apuração Ecredac Simplificada (e-CredAc, Portaria CAT
 * 207/2009): N linhas por competência (1 por categoria de operação geradora + 1 SOMA).
 * @param {string|number} id
 */
export async function buscarSumarizacaoEcredacApuracao(id) {
  return consultarSumarizacao(() => buscarSumarizacaoEcredacApuracaoApi(id));
}

/**
 * TXM-418/TXM-475 — créditos por nota do Ecredac Simplificado (grão do card/protótipo:
 * CNPJ, Competência, Fluxo, Documento, NCM, ICMS Suportado, Valor Confronto, ICMS Gera).
 * @param {string|number} id
 */
export async function buscarNotasEcredacApuracao(id) {
  return consultarSumarizacao(() => buscarNotasEcredacApuracaoApi(id));
}

/**
 * TXM-418/TXM-475 — exporta "Créditos por nota" (com os filtros atuais da tela) pra pasta
 * relatorios. Síncrono (sem correlationId/polling) — é a mesma consulta ao vivo da tela.
 * @param {string|number} id
 * @param {{cnpj?: string, busca?: string, competencia?: string, fluxo?: string, credito?: 'SIM'|'NAO'}} filtros
 */
export async function exportarNotasEcredacApuracao(id, filtros) {
  try {
    const res = await exportarNotasEcredacApuracaoApi(id, filtros);
    if (!res?.sucesso) {
      return { sucesso: false, mensagem: res?.mensagem || "Não foi possível exportar o CSV." };
    }
    return { sucesso: true, arquivo: res.arquivo, total: res.total };
  } catch (error) {
    return {
      sucesso: false,
      mensagem: error?.response?.data?.mensagem || "Não foi possível exportar o CSV.",
    };
  }
}

/**
 * TXM-479/TXM-594 — mapa NCM → Classificação sugerida (Insumo/Uso/Consumo). Falha "suave":
 * se der erro, devolve {} em vez de propagar — a classificação por NCM é um extra sobre a
 * lista de notas (que já carregou e é o essencial da tela), não deve derrubar a tela inteira
 * se a Tabela NCM estiver indisponível por algum motivo.
 * @param {string|number} id
 */
export async function buscarClassificacaoSugeridaEcredac(id) {
  try {
    const res = await buscarClassificacaoSugeridaEcredacApi(id);
    return res?.sucesso ? res.classificacoes || {} : {};
  } catch (error) {
    return {};
  }
}

/**
 * TXM-472 — NCMs da apuração CAT-42 (id é a filha ICMS_ST_CAT42, não a apuração ICMS pai)
 * com alíquota interna, MVA, Tipo e Classificação (Insumo/Uso/Consumo) sugerida da Tabela NCM
 * (TXM-594, via public.fn_tb_ncm_item_vigente no api-relatorios). `avisos` explica lacunas
 * (grupo/UF não resolvidos, cadastro divergente) pra tela nunca precisar adivinhar.
 * @param {string|number} id
 */
export async function buscarClassificacaoNcmApuracao(id) {
  try {
    const res = await buscarClassificacaoNcmApuracaoApi(id);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Classificação NCM indisponível.",
      };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      motor: res?.motor || "",
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      total: typeof res?.total === "number" ? res.total : 0,
      tabelaNcmDisponivel: Boolean(res?.tabelaNcmDisponivel),
      avisos: Array.isArray(res?.avisos) ? res.avisos : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar classificação NCM.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Consulta sumarização integral da apuração.
 * `opcoes.projeto` (TXM-558) troca o resultado padrão pelos itens utilizados daquele projeto; só é
 * repassado quando informado.
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [opcoes]
 */
export async function buscarSumarizacaoApuracao(id, opcoes) {
  return consultarSumarizacao(() =>
    opcoes === undefined ? buscarSumarizacaoApuracaoApi(id) : buscarSumarizacaoApuracaoApi(id, opcoes)
  );
}

/** Forma comum das duas consultas de sumarização (genérica e ICMS). */
async function consultarSumarizacao(chamar) {
  try {
    const res = await chamar();
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Sumarização indisponível.",
      };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      motor: res?.motor || "",
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      total: typeof res?.total === "number" ? res.total : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Drill-down de lançamentos CAT-42 por item (entrada+saída de uma competência).
 * @param {string|number} id
 * @param {string} competencia YYYY-MM
 */
export async function buscarLancamentosCat42Apuracao(id, competencia) {
  try {
    const res = await buscarLancamentosCat42ApuracaoApi(id, competencia);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Lançamentos indisponíveis para esta competência.",
      };
    }
    return {
      sucesso: true,
      competencia: res?.competencia || competencia,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-597 — lançamentos individuais (1 linha por documento) de uma competência+tipo da
 * apuração de Notas Fiscais.
 * @param {string|number} id
 * @param {string} competencia YYYY-MM
 * @param {string} tipo "Entrada"|"Saída"
 */
export async function buscarLancamentosNotasFiscaisApuracao(id, competencia, tipo) {
  try {
    const res = await buscarLancamentosNotasFiscaisApuracaoApi(id, competencia, tipo);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Lançamentos indisponíveis para esta competência.",
      };
    }
    return {
      sucesso: true,
      competencia: res?.competencia || competencia,
      tipo: res?.tipo || tipo,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-598 — lançamentos individuais (I200+I250) de uma conta contábil numa competência da
 * apuração ECD. Paginado — a fonte (I250) chega a dezenas de milhões de linhas por CNPJ.
 * @param {string|number} id
 * @param {{ competencia: string, conta: string, natureza?: string, busca?: string, pagina?: number, pageSize?: number }} params
 */
export async function buscarLancamentosEcdApuracao(id, params) {
  try {
    const res = await buscarLancamentosEcdApuracaoApi(id, params);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Lançamentos indisponíveis para esta conta/competência.",
      };
    }
    return {
      sucesso: true,
      competencia: res?.competencia || params.competencia,
      codCta: res?.codCta || params.conta,
      pagina: res?.pagina || 1,
      pageSize: res?.pageSize || 50,
      total: typeof res?.total === "number" ? res.total : 0,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-599 — lançamentos individuais de ajuste (M300, Adição/Exclusão) do fechamento anual
 * de um exercício da apuração ECF. Paginado.
 * @param {string|number} id
 * @param {{ ano: number, tipoLancamento?: string, busca?: string, pagina?: number, pageSize?: number }} params
 */
export async function buscarLancamentosEcfApuracao(id, params) {
  try {
    const res = await buscarLancamentosEcfApuracaoApi(id, params);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Lançamentos indisponíveis para este exercício.",
      };
    }
    return {
      sucesso: true,
      ano: res?.ano ?? params.ano,
      tipoLancamento: res?.tipoLancamento ?? params.tipoLancamento ?? null,
      pagina: res?.pagina || 1,
      pageSize: res?.pageSize || 50,
      total: typeof res?.total === "number" ? res.total : 0,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-600 — lançamentos de um bloco do SPED Contribuições (M200/M210/M600/M610/F100/C170)
 * numa competência. Devolve as 4 colunas da tela genérica do protótipo (descrição, registro,
 * natureza, valor) — o leiaute completo de cada registro não é exposto por esta tela.
 * @param {string|number} id
 * @param {{ competencia: string, bloco?: string, busca?: string, valorMin?: number, valorMax?: number, pagina?: number, pageSize?: number }} params
 */
export async function buscarLancamentosContribuicoesApuracao(id, params) {
  try {
    const res = await buscarLancamentosContribuicoesApuracaoApi(id, params);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Lançamentos indisponíveis para este bloco.",
      };
    }
    return {
      sucesso: true,
      competencia: res?.competencia ?? params.competencia,
      bloco: res?.bloco ?? params.bloco ?? null,
      pagina: res?.pagina || 1,
      pageSize: res?.pageSize || 50,
      total: typeof res?.total === "number" ? res.total : 0,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-476 — drill-down dos ajustes E111 (+ indicação de E112) de uma competência da
 * apuração ICMS. Coluna "Tipo" fica de fora — sem fonte confiável pra mapear o prefixo
 * de codAjApur; documentoFiscalVinculado (via E112) não validado com dado real.
 * @param {string|number} id
 * @param {string} competencia YYYY-MM
 */
export async function buscarLancamentosIcmsE111Apuracao(id, competencia) {
  try {
    const res = await buscarLancamentosIcmsE111ApuracaoApi(id, competencia);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Lançamentos indisponíveis para esta competência.",
      };
    }
    return {
      sucesso: true,
      competencia: res?.competencia || competencia,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Drill-down de lançamentos por conta contábil (paginado, server-side).
 * @param {string|number} id
 * @param {object} params
 */
export async function buscarLancamentosContaApuracao(id, params = {}) {
  const ecdContaContabil = String(params?.ecdContaContabil ?? params?.ecd ?? "").trim();
  if (!ecdContaContabil) {
    return { sucesso: false, mensagem: "Conta contábil inválida." };
  }

  try {
    const res = await buscarLancamentosContaApuracaoApi(id, params);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível carregar os lançamentos.",
        status: res.status,
      };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      conta: res?.conta || null,
      colunas: Array.isArray(res?.colunas) ? res.colunas : [],
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      paginacao: res?.paginacao || {
        pagina: params.pagina || 1,
        limite: params.limite || 10,
        total: 0,
        totalPaginas: 0,
      },
    };
  } catch (error) {
    const status = error?.response?.status;
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao consultar lançamentos da conta.";
    return { sucesso: false, mensagem, status };
  }
}

/**
 * Aplica a base legal a todas as competências de uma conta na sumarização.
 * `compoeProjeto` volta derivado do backend (true quando há base legal).
 * @param {string|number} id
 * @param {{ ecdContaContabil: string, baseLegalId: number|null }} params
 */
export async function atualizarBaseLegalSumarizacao(id, params) {
  const ecdContaContabil = String(params?.ecdContaContabil ?? "").trim();
  if (!ecdContaContabil) {
    return { sucesso: false, mensagem: "Conta contábil inválida." };
  }
  const baseLegalId = params?.baseLegalId ?? null;
  if (baseLegalId !== null && !Number.isInteger(baseLegalId)) {
    return { sucesso: false, mensagem: "Base legal inválida." };
  }

  try {
    const res = await atualizarBaseLegalSumarizacaoApi(id, {
      ecdContaContabil,
      baseLegalId,
    });
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível salvar a base legal.",
      };
    }
    return {
      sucesso: true,
      ecdContaContabil: res?.ecdContaContabil ?? ecdContaContabil,
      baseLegalId: res?.baseLegalId ?? baseLegalId,
      compoeProjeto: res?.compoeProjeto ?? baseLegalId !== null,
      linhasAtualizadas:
        typeof res?.linhasAtualizadas === "number" ? res.linhasAtualizadas : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao salvar a base legal.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-558 — ICMS-ST: aplica a base legal a todas as linhas de um NCM (NCM vazio é válido).
 * @param {string|number} id apuração filha (ICMS_ST_APURACAO)
 * @param {{ ncm: string, baseLegalId: number|null }} params
 */
export async function atualizarBaseLegalIcmsSt(id, params) {
  const ncm = String(params?.ncm ?? "").trim();
  const baseLegalId = params?.baseLegalId ?? null;
  if (baseLegalId !== null && !Number.isInteger(baseLegalId)) {
    return { sucesso: false, mensagem: "Base legal inválida." };
  }

  try {
    const res = await atualizarBaseLegalSumarizacaoApi(id, { ncm, baseLegalId });
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Não foi possível salvar a base legal." };
    }
    return {
      sucesso: true,
      ncm: res?.ncm ?? ncm,
      baseLegalId: res?.baseLegalId ?? baseLegalId,
      linhasAtualizadas: typeof res?.linhasAtualizadas === "number" ? res.linhasAtualizadas : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao salvar a base legal.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Catálogo de bases legais para os seletores das telas de apuração.
 * Devolve lista vazia em caso de erro — a tela continua utilizável, só sem opções.
 * @param {"CONCILIACAO_SPED"|"ESOCIAL"|"ICMS_ST"} tipo - Obrigatório: identifica de qual apuração é a tela chamadora.
 * @returns {Promise<{ sucesso: boolean, basesLegais: Array<object>, mensagem?: string }>}
 */
export async function buscarBasesLegais(tipo) {
  try {
    const res = await buscarBasesLegaisApi(tipo);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        basesLegais: [],
        mensagem: res.mensagem || "Não foi possível carregar as bases legais.",
      };
    }
    const lista = Array.isArray(res?.basesLegais)
      ? res.basesLegais
      : Array.isArray(res)
        ? res
        : [];
    return { sucesso: true, basesLegais: lista };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao carregar as bases legais.";
    return { sucesso: false, basesLegais: [], mensagem };
  }
}

/* ---------------------------------------------------------------------- */
/* TXM-336 — eSocial                                                       */
/* ---------------------------------------------------------------------- */

/**
 * Detalhe agrupado (comp × rubrica) da apuração eSocial. `opcoes.projeto` (TXM-558) troca o
 * resultado padrão pelos itens utilizados daquele projeto; só é repassado quando informado.
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [opcoes]
 */
export async function buscarAgrupadoEsocial(id, opcoes) {
  try {
    const res =
      opcoes === undefined ? await buscarAgrupadoEsocialApi(id) : await buscarAgrupadoEsocialApi(id, opcoes);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Detalhe eSocial indisponível." };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      total: typeof res?.total === "number" ? res.total : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao consultar detalhe eSocial.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Cards + série por competência da apuração eSocial. `opcoes.projeto` (TXM-558) soma só os itens
 * daquele projeto.
 * @param {string|number} id
 * @param {{ projeto?: number|string }} [opcoes]
 */
export async function buscarDashboardEsocial(id, opcoes) {
  try {
    const res =
      opcoes === undefined ? await buscarDashboardEsocialApi(id) : await buscarDashboardEsocialApi(id, opcoes);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Dashboard eSocial indisponível." };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      cards: res?.cards || {},
      serieCompetencia: Array.isArray(res?.serieCompetencia) ? res.serieCompetencia : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao consultar dashboard eSocial.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Catálogo de rúbricas da apuração eSocial (tela Rúbricas).
 * @param {string|number} id
 */
export async function buscarRubricasEsocial(id) {
  try {
    const res = await buscarRubricasEsocialApi(id);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Rúbricas indisponíveis." };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      total: typeof res?.total === "number" ? res.total : 0,
      // TXM-671: listas oficiais de incidência (droplists). API antiga sem o campo → vazias.
      dominios: {
        inss: Array.isArray(res?.dominios?.inss) ? res.dominios.inss : [],
        fgts: Array.isArray(res?.dominios?.fgts) ? res.dominios.fgts : [],
        irrf: Array.isArray(res?.dominios?.irrf) ? res.dominios.irrf : [],
      },
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao consultar rúbricas.";
    return { sucesso: false, mensagem };
  }
}

/**
 * PATCH de dados de rúbrica (base legal / verba férias / incidência). D5: não
 * invalida a apuração — efeito só no próximo reprocessamento.
 *
 * `baseLegalId` distingue três casos: chave ausente = não alterar; `null` = limpar
 * (tira a rúbrica do projeto); número = aplicar.
 * @param {string|number} id
 * @param {string} codigoRubrica
 * @param {{ baseLegalId?: number|null, verbaFerias?: boolean, flagIncidencia?: boolean }} params
 */
export async function atualizarRubricaEsocial(id, codigoRubrica, params = {}) {
  const codigo = String(codigoRubrica ?? "").trim();
  if (!codigo) {
    return { sucesso: false, mensagem: "Código da rúbrica inválido." };
  }
  const body = {};
  if ("baseLegalId" in params) {
    const baseLegalId = params.baseLegalId ?? null;
    if (baseLegalId !== null && !Number.isInteger(baseLegalId)) {
      return { sucesso: false, mensagem: "Base legal inválida." };
    }
    body.baseLegalId = baseLegalId;
  }
  if (typeof params.verbaFerias === "boolean") body.verbaFerias = params.verbaFerias;
  if (typeof params.flagIncidencia === "boolean") body.flagIncidencia = params.flagIncidencia;
  if (Object.keys(body).length === 0) {
    return {
      sucesso: false,
      mensagem: "Informe baseLegalId, verbaFerias e/ou flagIncidencia.",
    };
  }

  try {
    const res = await atualizarRubricaEsocialApi(id, codigo, body);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Não foi possível salvar a rúbrica." };
    }
    return {
      sucesso: true,
      codigoRubrica: res?.codigoRubrica ?? codigo,
      baseLegalId: res?.baseLegalId,
      compoeProjeto: res?.compoeProjeto,
      verbaFerias: res?.verbaFerias,
      flagIncidencia: res?.flagIncidencia,
      linhasAtualizadas: typeof res?.linhasAtualizadas === "number" ? res.linhasAtualizadas : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao salvar a rúbrica.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Drill-down bruto por rúbrica (paginado, server-side).
 * @param {string|number} id
 * @param {object} params
 */
export async function buscarLancamentosEsocial(id, params = {}) {
  const codigoRubrica = String(params?.codigoRubrica ?? "").trim();
  if (!codigoRubrica) {
    return { sucesso: false, mensagem: "Código da rúbrica inválido." };
  }
  try {
    const res = await buscarLancamentosEsocialApi(id, params);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível carregar os lançamentos.",
        status: res.status,
      };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      rubrica: res?.rubrica || null,
      colunas: Array.isArray(res?.colunas) ? res.colunas : [],
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      paginacao: res?.paginacao || {
        pagina: params.pagina || 1,
        limite: params.limite || 10,
        total: 0,
        totalPaginas: 0,
      },
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao consultar lançamentos.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Cálculo 20S por competência.
 * @param {string|number} id
 */
export async function buscarCalculo20sEsocial(id) {
  try {
    const res = await buscarCalculo20sEsocialApi(id);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Cálculo 20S indisponível." };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
      total: typeof res?.total === "number" ? res.total : 0,
      valorAtualizadoTotal:
        typeof res?.valorAtualizadoTotal === "number" ? res.valorAtualizadoTotal : 0,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao consultar Cálculo 20S.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Modo Sefip (D12) — agregado por competência (não por rúbrica).
 * @param {string|number} id
 */
export async function buscarSefipEsocial(id) {
  try {
    const res = await buscarSefipEsocialApi(id);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Sefip indisponível." };
    }
    return {
      sucesso: true,
      apuracao: res?.apuracao || null,
      linhas: Array.isArray(res?.linhas) ? res.linhas : [],
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao consultar Sefip.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Enfileira exportação eSocial (Kafka). Resposta imediata — arquivo na pasta do servidor.
 * @param {string|number} id
 * @param {{ tipo: 'agrupado'|'rubricas'|'20s'|'lancamentos', formato?: string, modoXlsx?: string,
 *   filtros?: { codrubr: string[], colunas: string[] } }} params
 *   `filtros` (TXM-671) só vale para tipo='rubricas': rúbricas filtradas na tela + chaves das
 *   colunas visíveis (ver DetalheEsocial/utils/gruposColunasRubricas.util.js).
 */
export async function exportarEsocial(id, params) {
  try {
    const res = await exportarEsocialApi(id, params);
    if (res?.sucesso === false) {
      return { sucesso: false, mensagem: res.mensagem || "Não foi possível exportar." };
    }
    return {
      sucesso: true,
      mensagem: res?.mensagem || "O arquivo será entregue em breve.",
      correlationId: res?.correlationId,
      status: res?.status,
      reaproveitado: Boolean(res?.reaproveitado),
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem || error?.message || "Erro ao enfileirar exportação.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Enfileira exportação na api-relatorios (Kafka). Resposta imediata — arquivo na pasta do servidor.
 * @param {string|number} id
 * @param {{ tipo: 'agrupado'|'lancamentos'|'resumo_geral', filtros?: object, formato?: 'csv'|'xlsx', modoXlsx?: 'unico'|'por-ano' }} params
 * `formato` — relevante para tipo="agrupado" e tipo="lancamentos" (achado 2026-09-14: esta
 * função só repassava formato para "lancamentos", descartando o "csv" que as abas do TXM-418
 * mandam pra tipo="agrupado" antes mesmo de chegar na api-relatorios — mesma suposição
 * desatualizada do TXM-325 item 01, só que duplicada nesta camada). `modoXlsx` continua só
 * relevante para tipo="lancamentos" (conceito de múltiplos anos, que "agrupado" não tem).
 * `resumo_geral` ignora `filtros` — é sempre o total da apuração (ver Detalhe/index.js).
 */
export async function exportarSumarizacaoApuracao(id, params) {
  const tipo = String(params?.tipo || "").trim().toLowerCase();
  if (
    tipo !== "agrupado" &&
    tipo !== "lancamentos" &&
    tipo !== "resumo_geral" &&
    tipo !== "arquivo_digital"
  ) {
    return { sucesso: false, mensagem: "Tipo de exportação inválido." };
  }

  const body = {
    tipo,
    filtros:
      tipo === "resumo_geral"
        ? {}
        : params?.filtros && typeof params.filtros === "object"
          ? params.filtros
          : {},
  };
  if (tipo === "agrupado" || tipo === "lancamentos") {
    if (params?.formato) body.formato = params.formato;
  }
  if (tipo === "lancamentos") {
    if (params?.modoXlsx) body.modoXlsx = params.modoXlsx;
  }

  try {
    const res = await exportarSumarizacaoApuracaoApi(id, body);
    if (!res?.sucesso) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível solicitar a exportação.",
      };
    }
    return {
      sucesso: true,
      mensagem: res.mensagem || "O arquivo será entregue em breve.",
      correlationId: res.correlationId,
      status: res.status,
      reaproveitado: Boolean(res.reaproveitado),
      // @deprecated (fase 5 do desenho de rastreio) — o front novo usa `arquivos[]` do status.
      nomeArquivo: res.nomeArquivo,
      nomeArquivos: Array.isArray(res.nomeArquivos) ? res.nomeArquivos : [res.nomeArquivo].filter(Boolean),
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar exportação da sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * @deprecated Download por nome de arquivo (rota antiga, fase 5 do desenho de rastreio).
 * Use `consultarStatusExportacao` + `baixarArquivoExportacao`.
 */
export { baixarExportacaoApuracao };

/**
 * Estado de um job de exportação (sumarização ou eSocial) pelo `correlationId`.
 *
 * Só repassa `mensagem` (= mensagem_usuario no servidor, segura pra UI) — o erro técnico
 * nunca vem nessa resposta. Em falha HTTP, devolve `httpStatus` pro chamador distinguir
 * "job não existe/não é seu" (404, terminal) de falha transitória de rede/servidor.
 * @param {string|number} id
 * @param {string} correlationId
 * @returns {Promise<
 *   | { sucesso: true, status: string, percentual: number|null, etapaAtual: number, etapasTotal: number|null,
 *       descricaoEtapa: string|null, mensagem: string|null, arquivos: Array<{ indice: number, nome: string, bytes?: number }> }
 *   | { sucesso: false, httpStatus: number|null }
 * >}
 */
export async function consultarStatusExportacao(id, correlationId) {
  try {
    const res = await consultarStatusExportacaoApi(id, correlationId);
    if (!res || res.sucesso === false || !res.status) {
      return { sucesso: false, httpStatus: null };
    }
    // null = progresso indeterminado (etapasTotal desconhecido) — não inventar número.
    const percentual =
      res.percentual == null || !Number.isFinite(Number(res.percentual))
        ? null
        : Math.max(0, Math.min(100, Number(res.percentual)));
    return {
      sucesso: true,
      status: String(res.status),
      percentual,
      etapaAtual: Number(res.etapaAtual) || 0,
      etapasTotal: res.etapasTotal == null ? null : Number(res.etapasTotal),
      descricaoEtapa: res.descricaoEtapa || null,
      mensagem: res.mensagem || null,
      arquivos: Array.isArray(res.arquivos)
        ? res.arquivos.map((a, i) => ({
            indice: Number.isInteger(a?.indice) ? a.indice : i,
            nome: a?.nome || `exportacao-${i + 1}`,
            bytes: a?.bytes,
          }))
        : [],
    };
  } catch (error) {
    return { sucesso: false, httpStatus: error?.response?.status ?? null };
  }
}

/**
 * Baixa um arquivo de job concluído. A resposta de erro vem como Blob (responseType),
 * então a mensagem é escolhida pelo status HTTP aqui, não lida do corpo.
 * @param {string|number} id
 * @param {string} correlationId
 * @param {number} indice
 * @returns {Promise<{ sucesso: true, blob: Blob } | { sucesso: false, httpStatus: number|null, mensagem: string }>}
 */
export async function baixarArquivoExportacao(id, correlationId, indice) {
  try {
    const blob = await baixarArquivoExportacaoApi(id, correlationId, indice);
    return { sucesso: true, blob };
  } catch (error) {
    const httpStatus = error?.response?.status ?? null;
    const mensagem =
      httpStatus === 410
        ? "O arquivo expirou e não está mais disponível. Exporte novamente."
        : httpStatus === 404
          ? "Arquivo não encontrado. Exporte novamente."
          : "Não foi possível baixar o arquivo.";
    return { sucesso: false, httpStatus, mensagem };
  }
}

/**
 * Reprocessa sumarização com novos filtros de exclusão (substitui os anteriores).
 * @param {string|number} id
 * @param {{ termos_exclusao?: string[], campos?: string[] }} params
 */
export async function reprocessarSumarizacaoApuracao(id, params = {}) {
  const termos = Array.isArray(params?.termos_exclusao)
    ? params.termos_exclusao
    : [];
  const body = { termos_exclusao: termos };
  if (Array.isArray(params?.campos) && params.campos.length > 0) {
    body.campos = params.campos;
  }

  try {
    const res = await reprocessarSumarizacaoApuracaoApi(id, body);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível reprocessar a sumarização.",
      };
    }
    return {
      sucesso: true,
      mensagem:
        res?.mensagem || "Reprocessamento da sumarização enfileirado.",
      correlationId: res?.correlationId,
      sumarizacaoFiltros: res?.sumarizacaoFiltros,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar reprocessamento da sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * TXM-325 item 08 — reprocessa anos selecionados de uma apuração em divergência
 * (TRUNCATE da z_* do ano + regenera; disponível só quando status = divergencia).
 * @param {string|number} id
 * @param {{ anos: number[], contas_criterio?: 'resultado'|'todas' }} params
 */
export async function reprocessarAnosApuracao(id, params = {}) {
  const anos = Array.isArray(params?.anos)
    ? params.anos.map((a) => Number(a)).filter((a) => Number.isFinite(a))
    : [];
  if (anos.length === 0) {
    return { sucesso: false, mensagem: "Selecione ao menos um ano para reprocessar." };
  }

  const criterio = String(params?.contas_criterio ?? "").trim();
  if (criterio !== "resultado" && criterio !== "todas") {
    return {
      sucesso: false,
      mensagem: 'contas_criterio é obrigatório e deve ser "resultado" ou "todas".',
    };
  }

  try {
    const res = await reprocessarAnosApuracaoApi(id, {
      anos,
      contas_criterio: criterio,
    });
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível reprocessar os anos selecionados.",
      };
    }
    return {
      sucesso: true,
      mensagem: res?.mensagem || "Reprocessamento enfileirado.",
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar reprocessamento por ano.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Dispara sumarização inicial ou retry (apuração já concluída, sumarização pendente/erro).
 * @param {string|number} id
 */
export async function processarSumarizacaoApuracao(id) {
  try {
    const res = await processarSumarizacaoApuracaoApi(id);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível processar a sumarização.",
      };
    }
    return {
      sucesso: true,
      mensagem:
        res?.mensagem || "Sumarização enfileirada para processamento.",
      correlationId: res?.correlationId,
      sumarizacaoStatus: res?.sumarizacaoStatus,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar processamento da sumarização.";
    return { sucesso: false, mensagem };
  }
}

/**
 * Reaplica SELIC nos lançamentos e enfileira reprocesso da sumarização.
 * @param {string|number} id
 */
export async function atualizarSelicApuracao(id) {
  try {
    const res = await atualizarSelicApuracaoApi(id);
    if (res?.sucesso === false) {
      return {
        sucesso: false,
        mensagem: res.mensagem || "Não foi possível atualizar a SELIC.",
      };
    }
    return {
      sucesso: true,
      mensagem:
        res?.mensagem ||
        "SELIC atualizada nos lançamentos. Sumarização em processamento.",
      correlationId: res?.correlationId,
      sumarizacaoStatus: res?.sumarizacaoStatus,
      selic: res?.selic,
    };
  } catch (error) {
    const mensagem =
      error?.response?.data?.mensagem ||
      error?.message ||
      "Erro ao solicitar atualização de SELIC.";
    return { sucesso: false, mensagem };
  }
}

